import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Three postures, three answers — a static side-by-side comparison.
 *
 * Replaces the earlier interactive triangle on the homepage. The triangle was
 * doing UI work as a metaphor; here the three postures live as full-width
 * comparable cards so the weights and tradeoffs read at a glance without a
 * click. The triangle metaphor is carried verbally by the section heading
 * ("Your priority weights are the axis").
 */

type Posture = {
  id: string;
  label: string;
  weights: { cost: number; conversion: number; risk: number };
  tagline: string;
  posture: string;
  tradeoff: string;
  emphasis: 'cost' | 'conversion' | 'risk';
};

const postures: Posture[] = [
  {
    id: 'conversion',
    label: 'Conversion-leaning',
    weights: { cost: 20, conversion: 60, risk: 20 },
    tagline: 'Pull harder on approval. Accept some fee drag.',
    posture:
      'Widen the authorization surface segment by segment. The optimization prefers routes with the highest completion odds for each buyer, and accepts higher processing economics where they buy approval.',
    tradeoff: 'Blended cost rises on the segments that were already clearing cleanly.',
    emphasis: 'conversion',
  },
  {
    id: 'balanced',
    label: 'Balanced',
    weights: { cost: 33, conversion: 34, risk: 33 },
    tagline: 'No axis dominates. Keep all three healthy.',
    posture:
      'Operate the tension as given. No axis concedes to the other two. The optimization composes the stack so approval, economics, and dispute control stay in balance across every segment.',
    tradeoff: 'Gives up outsized movement on any one axis in exchange for sustained health on all three.',
    emphasis: 'cost',
  },
  {
    id: 'risk',
    label: 'Risk-leaning',
    weights: { cost: 25, conversion: 25, risk: 50 },
    tagline: 'Tighten liability posture. Narrow the method mix.',
    posture:
      'Tighten the authorization surface. The optimization steps up authentication where dispute pressure is highest and narrows the composition toward the lowest-dispute rails for exposed segments.',
    tradeoff: 'A slice of approval on the riskier segments where friction is now higher.',
    emphasis: 'risk',
  },
];

const COST_COLOR       = '#60A5FA';
const CONVERSION_COLOR = '#34D399';
const RISK_COLOR       = '#A78BFA';

const dominantOf = (p: Posture) => p.emphasis;

const PostureBar: React.FC<{
  label: string;
  value: number;
  color: string;
  highlight: boolean;
}> = ({ label, value, color, highlight }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '64px 1fr 22px', alignItems: 'center', gap: 1 }}>
    <Typography
      sx={{
        fontSize: '0.58rem',
        fontWeight: 400,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: highlight ? color : 'rgba(255,255,255,0.5)',
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        height: 9,
        borderRadius: 5,
        backgroundColor: 'rgba(255,255,255,0.09)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${value}%`,
          background: `linear-gradient(90deg, ${color} 0%, ${color}ee 55%, ${color}bb 100%)`,
          borderRadius: 5,
          boxShadow: highlight ? `0 0 14px ${color}99, 0 0 6px ${color}66` : 'none',
        }}
      />
    </Box>
    <Typography
      sx={{
        textAlign: 'right',
        color: highlight ? color : 'rgba(255,255,255,0.78)',
        fontSize: '0.72rem',
        fontWeight: 400,
      }}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
  </Box>
);

const PostureColumn: React.FC<{ posture: Posture; featured?: boolean }> = ({
  posture,
  featured = false,
}) => {
  const dominant = dominantOf(posture);
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1.2,
        border: '1px solid',
        borderColor: featured ? 'rgba(62,161,255,0.32)' : 'rgba(255,255,255,0.08)',
        background: featured
          ? 'linear-gradient(180deg, rgba(62,161,255,0.10) 0%, rgba(62,161,255,0.015) 100%)'
          : 'rgba(255,255,255,0.025)',
        p: { xs: 2, md: 2.4 },
        gap: 1.6,
      }}
    >
      {/* Header — label + weight chips */}
      <Box>
        <Typography
          sx={{
            fontSize: '0.58rem',
            fontWeight: 400,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: featured ? '#bfdbfe' : 'rgba(255,255,255,0.5)',
          }}
        >
          Posture
        </Typography>
        <Typography
          sx={{
            mt: 0.4,
            fontSize: '1.05rem',
            fontWeight: 400,
            color: '#ffffff',
            letterSpacing: '-0.005em',
          }}
        >
          {posture.label}
        </Typography>
        <Box
          sx={{
            mt: 0.9,
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.6,
          }}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          <Typography component="span" sx={{ fontSize: '0.92rem', fontWeight: 400, color: COST_COLOR }}>
            {posture.weights.cost}
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>·</Typography>
          <Typography component="span" sx={{ fontSize: '0.92rem', fontWeight: 400, color: CONVERSION_COLOR }}>
            {posture.weights.conversion}
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>·</Typography>
          <Typography component="span" sx={{ fontSize: '0.92rem', fontWeight: 400, color: RISK_COLOR }}>
            {posture.weights.risk}
          </Typography>
          <Typography
            component="span"
            sx={{
              ml: 0.8,
              fontSize: '0.55rem',
              fontWeight: 400,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.35)',
            }}
          >
            cost · conv · risk
          </Typography>
        </Box>
      </Box>

      {/* Axis bars */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
        <PostureBar label="Cost"       value={posture.weights.cost}       color={COST_COLOR}       highlight={dominant === 'cost'} />
        <PostureBar label="Conv"       value={posture.weights.conversion} color={CONVERSION_COLOR} highlight={dominant === 'conversion'} />
        <PostureBar label="Risk"       value={posture.weights.risk}       color={RISK_COLOR}       highlight={dominant === 'risk'} />
      </Box>

      {/* Tagline */}
      <Typography
        sx={{
          fontSize: '0.86rem',
          fontWeight: 400,
          color: '#ffffff',
          lineHeight: 1.45,
          letterSpacing: '-0.005em',
        }}
      >
        {posture.tagline}
      </Typography>

      {/* Posture body */}
      <Typography
        sx={{
          fontSize: '0.82rem',
          fontWeight: 400,
          color: 'rgba(255,255,255,0.78)',
          lineHeight: 1.55,
        }}
      >
        {posture.posture}
      </Typography>

      {/* Tradeoff at the bottom */}
      <Box
        sx={{
          mt: 'auto',
          pt: 1.4,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          gap: 1.1,
          alignItems: 'flex-start',
        }}
      >
        <Box
          aria-hidden
          sx={{
            mt: 0.55,
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: '#fbbf24',
            boxShadow: '0 0 8px rgba(251,191,36,0.55)',
            flexShrink: 0,
          }}
        />
        <Box>
          <Typography
            sx={{
              fontSize: '0.56rem',
              fontWeight: 400,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            What this posture gives up
          </Typography>
          <Typography
            sx={{
              mt: 0.4,
              fontSize: '0.78rem',
              fontWeight: 400,
              color: 'rgba(255,255,255,0.78)',
              lineHeight: 1.5,
            }}
          >
            {posture.tradeoff}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const PaymentTriangle: React.FC = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 1.6,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #2a4574 0%, #20355e 60%, #1a2c52 100%)',
        border: '1px solid rgba(255,255,255,0.10)',
        boxShadow: '0 30px 80px rgba(20,40,80,0.32)',
        p: { xs: 2.5, md: 3.4 },
      }}
    >
      {/* Ambient glow */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -160,
          right: -120,
          width: 360,
          height: 360,
          borderRadius: '50%',
          background: 'radial-gradient(closest-side, rgba(62,161,255,0.16), transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Header */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: { xs: 2.4, md: 3 },
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: 400,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.45)',
            }}
          >
            Three postures, three answers
          </Typography>
          <Typography
            sx={{
              mt: 0.6,
              fontSize: '1.05rem',
              fontWeight: 400,
              color: '#ffffff',
              letterSpacing: '-0.005em',
            }}
          >
            How the optimization shifts when the weights shift.
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '0.72rem',
            color: 'rgba(255,255,255,0.6)',
            fontWeight: 400,
            maxWidth: 320,
            textAlign: { xs: 'left', md: 'right' },
            lineHeight: 1.5,
          }}
        >
          The middle card is where most pilots start. Either wing is what the optimization composes when the priorities lean.
        </Typography>
      </Box>

      {/* Three posture columns */}
      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: { xs: 2, md: 2.4 },
          alignItems: 'stretch',
        }}
      >
        {postures.map((p) => (
          <PostureColumn key={p.id} posture={p} featured={p.id === 'balanced'} />
        ))}
      </Box>
    </Box>
  );
};

export default PaymentTriangle;
